import { splitCombinedSpaceTenantLabel, splitHost } from "./spaceDomain";
import { isValidSlug, type Slug } from "./slug";

// Dot-joined hosts can use two-character DNS labels; combined hosts need three so their `--` separator does not occupy IDNA's reserved third and fourth positions.
export function isValidSpaceHostSlug(
  slug: string,
  combinedSpaceTenantHosts: boolean
): slug is Slug {
  return (
    isValidSlug(slug) ||
    (!combinedSpaceTenantHosts && /^[a-z0-9]{2}$/.test(slug))
  );
}

export function parseSpaceUrl(
  spaceUrl: URL,
  combinedSpaceTenantHosts: boolean
): { spaceSlug: string; tenantOrigin: string } | null {
  if (
    spaceUrl.username ||
    spaceUrl.password ||
    spaceUrl.hostname.split(".").some((label) => !label)
  ) {
    return null;
  }
  const split = splitHost(spaceUrl.hostname);
  if (!split) return null;

  let spaceSlug = split.label;
  let tenantHostname = split.parent;
  if (combinedSpaceTenantHosts) {
    const combined = splitCombinedSpaceTenantLabel(split.label);
    if (!combined) return null;
    spaceSlug = combined.spaceLabel;
    tenantHostname = `${combined.tenantLabel}.${split.parent}`;
  }

  if (!isValidSpaceHostSlug(spaceSlug, combinedSpaceTenantHosts)) return null;
  const tenantUrl = new URL(spaceUrl);
  tenantUrl.hostname = tenantHostname;
  return { spaceSlug, tenantOrigin: tenantUrl.origin };
}

/**
 * Extract the space slug from a Host header when subdomain isolation is
 * enabled. Returns null if subdomain isolation is not configured or the host
 * doesn't match the wildcard pattern.
 *
 * Dot-joined:   "acme.tenant.3b.run" → "acme"
 * Combined:     "acme--tenant.3b.run" → "acme" (tenant label "tenant")
 */
export function parseSpaceSlug(
  host: string,
  wildcardDomain: string,
  combinedSpaceTenantLabel?: string
): string | null {
  if (!wildcardDomain) return null;

  const hostname = host.split(":")[0]!.toLowerCase();
  const domainSuffix = `.${wildcardDomain.toLowerCase()}`;
  if (!hostname.endsWith(domainSuffix)) return null;

  let subdomain = hostname.slice(0, -domainSuffix.length);
  if (!subdomain) return null;

  if (combinedSpaceTenantLabel) {
    const combined = splitCombinedSpaceTenantLabel(subdomain);
    if (!combined || combined.tenantLabel !== combinedSpaceTenantLabel) {
      return null;
    }
    subdomain = combined.spaceLabel;
  }

  return subdomain || null;
}
